/**
 * MacroVisualBars — affichage visuel P/L/G + calories après scan.
 *
 * Props :
 *   analysis : { total_calories, total_protein_g, total_carbs_g, total_fats_g, data_source }
 *   compact  : layout compact (sur les cards repas)
 *
 * Anti-crash : tous les nombres sont sécurisés (0 si NaN).
 */
import React from 'react';
import { Flame, Beef, Wheat, Droplet } from 'lucide-react';
import FatSecretBadge from './FatSecretBadge';

const safeNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const MacroBar = ({ label, value, max, color, icon: Icon, unit = 'g' }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-1" data-testid={`macro-bar-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-wider font-bold">
          <Icon className="w-3 h-3" style={{ color }} />
          {label}
        </span>
        <span className="font-bold" style={{ color }}>
          {value.toFixed(0)}{unit}
        </span>
      </div>
      <div className="h-2 rounded-full bg-secondary/50 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

const MacroVisualBars = ({ analysis, compact = false }) => {
  if (!analysis) return null;

  const cal = safeNum(analysis.total_calories);
  const prot = safeNum(analysis.total_protein_g);
  const carbs = safeNum(analysis.total_carbs_g);
  const fats = safeNum(analysis.total_fats_g);

  // Reference targets for visual bars (typical full meal)
  const protMax = 50;
  const carbsMax = 80;
  const fatsMax = 35;

  return (
    <div className={`space-y-3 ${compact ? '' : 'p-4 rounded-xl bg-secondary/30 border border-border'}`} data-testid="macro-visual-bars">
      {!compact && (
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h4 className="font-heading text-sm uppercase tracking-tight">Macros analysés</h4>
          <FatSecretBadge analysis={analysis} variant="full" showAiBadge />
        </div>
      )}
      {compact && analysis.data_source === 'fatsecret_canada' && (
        <div className="flex justify-end -mt-1 mb-1">
          <FatSecretBadge analysis={analysis} variant="mini" />
        </div>
      )}

      {/* Calories — hero number */}
      <div className="flex items-baseline gap-2 pb-2 border-b border-border/50">
        <Flame className="w-4 h-4 text-orange-400" />
        <span className="text-2xl font-heading font-bold text-orange-400 tracking-tight">
          {cal.toFixed(0)}
        </span>
        <span className="text-xs text-muted-foreground uppercase font-bold tracking-wider">kcal</span>
      </div>

      {/* P / L / G bars */}
      <div className="space-y-2.5">
        <MacroBar label="Protéines" value={prot} max={protMax} color="#f97316" icon={Beef} />
        <MacroBar label="Glucides" value={carbs} max={carbsMax} color="#22d3ee" icon={Wheat} />
        <MacroBar label="Lipides" value={fats} max={fatsMax} color="#facc15" icon={Droplet} />
      </div>
    </div>
  );
};

export default MacroVisualBars;
